<script setup lang="ts">
/**
 * Title row for a page, a panel or a document section: heading on the inline
 * start, actions on the inline end, optional description underneath. Used
 * everywhere so heading levels and action placement stay consistent — an
 * approver should never have to hunt for the Approve button.
 */

withDefaults(
  defineProps<{
    title: string
    description?: string
    /** Heading level. Pick the one the document outline needs, not the size. */
    level?: 1 | 2 | 3 | 4
    /** Visual size, decoupled from `level` so semantics stay correct. */
    size?: 'sm' | 'md' | 'lg'
    /** Rule under the header; use inside panels, not on page titles. */
    divider?: boolean
    /** Sticky within a scrolling panel, e.g. a document lines section. */
    sticky?: boolean
  }>(),
  { description: '', level: 2, size: 'md' },
)
</script>

<template>
  <div
    class="e-section-header"
    :class="[
      `e-section-header--${size}`,
      { 'e-section-header--divider': divider, 'e-section-header--sticky': sticky },
    ]"
  >
    <div class="e-section-header__text">
      <component :is="`h${level}`" class="e-section-header__title">
        <slot name="title">{{ title }}</slot>
      </component>
      <p v-if="description !== '' || $slots.description" class="e-section-header__description">
        <slot name="description">{{ description }}</slot>
      </p>
    </div>

    <div v-if="$slots.actions" class="e-section-header__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style>
.e-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--erpx-space-4);
  min-width: 0;
}

.e-section-header--divider {
  padding-block-end: var(--erpx-space-3);
  border-block-end: 1px solid var(--erpx-color-border-subtle);
}

.e-section-header--sticky {
  position: sticky;
  inset-block-start: 0;
  z-index: var(--erpx-z-sticky);
  background-color: var(--erpx-color-surface-raised);
}

.e-section-header__text {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-1);
  min-width: 0;
}

.e-section-header__title {
  margin: 0;
  color: var(--erpx-color-text-primary);
  font-weight: var(--erpx-weight-semibold);
  line-height: var(--erpx-leading-tight);
  letter-spacing: var(--erpx-tracking-tight);
}

.e-section-header--sm .e-section-header__title {
  font-size: var(--erpx-text-sm);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
  color: var(--erpx-color-text-secondary);
}

.e-section-header--md .e-section-header__title { font-size: var(--erpx-text-lg); }
.e-section-header--lg .e-section-header__title { font-size: var(--erpx-text-2xl); }

.e-section-header__description {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-sm);
  line-height: var(--erpx-leading-snug);
}

.e-section-header__actions {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  flex: none;
}
</style>
